import { Form, Input, Select, Upload, Image, Button } from "antd";
import { LinkOutlined, UploadOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedTenantId } from "../../../store/user/selectors";
import Modal from "../../../components/generic/Modal";
import { StyledPosterModalDiv } from "./styles";
import { savePoster, updatePosterKey } from "../../../store/poster/thunks";
import { uploadFile } from "../../../utils/uploadFile";
import { posterTypesOptions } from "./hepler";
import { FILE_BASE_URL } from "../../../config";
import PrintableImage from "../../../components/shared/PrintableImage";
import Video from "../../../components/shared/Video";

const { TextArea } = Input;

const AddEditModal = (props) => {
  const { selectedPoster = {}, handleCancel } = props;

  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState("");

  const selectedTenantId = useSelector(selectSelectedTenantId);

  const isView = useMemo(() => !!selectedPoster?.id, [selectedPoster]);

  const selectedType = Form.useWatch("type", form);

  useEffect(() => {
    if (isView) return;
    setPreview("");
    form.setFieldsValue({
      file: null,
    });
  }, [selectedType]);

  useEffect(() => {
    if (selectedPoster?.id) {
      form.setFieldsValue({
        type: selectedPoster?.type,
        content: selectedPoster?.content,
      });

      setPreview(`${FILE_BASE_URL}${selectedPoster?.content || ""}`);
    }
  }, [selectedPoster]);

  const handleUploadChange = useCallback(({ fileList: newFileList }) => {
    const file = newFileList?.[0]?.originFileObj;

    if (file) setPreview(URL.createObjectURL(file));
    else setPreview("");
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      try {
        setSubmitting(true);

        let { type, content } = values;

        if (type === "photo" || type === "video") {
          const file = values?.file?.fileList?.[0]?.originFileObj;

          if (!file) return;

          const url = await uploadFile({
            file,
            fields: {
              tenant_id: selectedTenantId,
            },
          });

          content = url;
        }

        await dispatch(
          savePoster({
            type,
            content,
            tenant_id: selectedTenantId,
          }),
        );

        handleCancel();
      } finally {
        setSubmitting(false);
      }
    },
    [handleCancel, selectedTenantId],
  );

  const onError = useCallback(async () => {
    return await dispatch(
      updatePosterKey({
        poster_id: selectedPoster?.id,
        tenant_id: selectedPoster?.tenant_id,
      }),
    );
  }, [selectedPoster]);

  return (
    <Modal
      isProcessing={submitting}
      title={isView ? "View Poster" : "Save Poster"}
      onCancel={handleCancel}
      footer={!isView ? undefined : null}
      width={600}
      onOk={() => form.submit()}
      open
    >
      <StyledPosterModalDiv>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={submitting || isView}
          initialValues={{
            type: "text",
          }}
        >
          <Form.Item
            label="Type"
            name="type"
            rules={[
              {
                required: true,
                message: "Please select type",
              },
            ]}
          >
            <Select placeholder="Select type" options={posterTypesOptions} />
          </Form.Item>

          {selectedType === "text" && (
            <Form.Item
              label="Content"
              name="content"
              rules={[
                {
                  required: true,
                  message: "Please enter content",
                },
              ]}
            >
              <TextArea placeholder="Please enter content" />
            </Form.Item>
          )}

          {selectedType === "link" && (
            <Form.Item
              label="URL Link"
              name="content"
              rules={[
                {
                  required: true,
                  message: "Please enter a URL",
                },
              ]}
            >
              <Input
                prefix={<LinkOutlined />}
                placeholder="https://example.com"
                type="url"
              />
            </Form.Item>
          )}

          {(selectedType === "photo" || selectedType === "video") && (
            <>
              {!isView && (
                <Form.Item
                  label={selectedType === "photo" ? "Photo" : "Video"}
                  rules={[
                    {
                      required: true,
                      message: "Please Select File",
                    },
                  ]}
                  name="file"
                >
                  <Upload
                    beforeUpload={() => false}
                    maxCount={1}
                    onChange={handleUploadChange}
                    accept={selectedType === "photo" ? "image/*" : "video/*"}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>Click to Upload</Button>
                  </Upload>
                </Form.Item>
              )}
              {preview && (
                <div className="preview">
                  {selectedType === "photo" ? (
                    <PrintableImage
                      onError={onError}
                      src={preview}
                      alt="preview"
                    />
                  ) : (
                    <Video fileUrl={preview} onError={onError} />
                  )}
                </div>
              )}
            </>
          )}
        </Form>
      </StyledPosterModalDiv>
    </Modal>
  );
};

export default AddEditModal;
